import axios from 'axios';
import authService from './authService';
import API_URL from '../config';
import logger from '../utils/logger';
import { flushDraftToStorage } from '../utils/ncDraftStore'; // #145

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token em todas as requisições
api.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('[API] Token não encontrado para requisição:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Endpoints de auth que NÃO devem disparar refresh-em-401:
//  - /auth/login   → 401 = credencial errada (não sessão expirada)
//  - /auth/refresh → evita recursão se o próprio refresh devolver 401
//  - /auth/logout  → best-effort; não faz sentido renovar para deslogar
function _isAuthEndpoint(url = '') {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout')
  );
}

// Sessão 19 — Interceptor de resposta com REFRESH transparente.
//
// Bug que estava resolvido aqui antes:
//   if (status === 401) { authService.logout(); window.location.href = '/'; }
//
// Sintoma reportado pelo usuário (Sessão 19): "ao salvar uma edição de NC,
// após determinado tempo com ela aberta, o sistema fecha e volta para a tela
// de login. O que foi editado não é salvo."
//
// Causa: o access token tem vida curta (30 min — auth_service.py:31). Ao
// expirar, o save retornava 401, o interceptor antigo deslogava IMEDIATAMENTE
// sem tentar renovar — e como `editarAnalise` (api.js editarAnalise) usa
// axios.put, passava por este interceptor (o fetchInterceptor cobre apenas
// window.fetch, não XHR/axios). Resultado: edição perdida.
//
// Fix: em 401, tentar single-flight refresh via authService.refreshAccessToken()
// e refazer a request original com o novo token. Só desloga se o refresh
// também falhar (refresh_token expirado >7 dias ou revogado por logout em
// outra aba). Marca `__isRetry` para evitar loop infinito se a request
// renovada também voltar 401.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response && error.response.status;
    const config = error.config || {};
    const url = config.url || '';

    // Casos onde NÃO tentamos refresh:
    //   - status ≠ 401 (não é problema de auth)
    //   - endpoint de auth (login/refresh/logout)
    //   - já é uma retry (evita loop)
    //   - não há refresh_token no storage (sem com que tentar)
    if (
      status !== 401 ||
      _isAuthEndpoint(url) ||
      config.__isRetry ||
      !authService.getRefreshToken()
    ) {
      // Quando não dá pra recuperar e é 401 de endpoint protegido (não-auth),
      // mantém o comportamento anterior de encerrar sessão.
      if (status === 401 && !_isAuthEndpoint(url)) {
        flushDraftToStorage(); // #145 — preserva rascunho antes do logout forçado
        authService.logout();
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
      }
      return Promise.reject(error);
    }

    // Tenta renovar o access token (single-flight no authService — fetch e
    // axios compartilham a mesma promise pendente).
    try {
      await authService.refreshAccessToken();
    } catch (refreshErr) {
      // Refresh falhou — refresh_token expirado/revogado. Encerra sessão.
      flushDraftToStorage(); // #145 — preserva rascunho antes do logout forçado
      authService.logout();
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
      return Promise.reject(error);
    }

    // Refresh OK. Refaz a request original com o novo access token.
    // __isRetry impede que outro 401 no retry dispare novo refresh em loop.
    const novoToken = authService.getToken();
    const retryConfig = {
      ...config,
      __isRetry: true,
      headers: {
        ...(config.headers || {}),
        Authorization: `Bearer ${novoToken}`,
      },
    };
    return api(retryConfig);
  }
);

// #78b: monta os query params do export a partir dos filtros da tela (mesma
// semântica do listNCs — só os definidos, ignora 'todos'). Exportado pra o
// NCList montar a URL da rota de PDF (/exportar-ncs?...).
export function buildExportParams(filtros = {}) {
  const params = {};
  const add = (k, v) => {
    if (v !== undefined && v !== null && v !== '' && v !== 'todos') params[k] = v;
  };
  add('status', filtros.status);
  add('severidade', filtros.severidade);
  add('origem_tipo', filtros.origem_tipo);
  add('fornecedor_id', filtros.fornecedor_id);
  add('cliente_emissor', filtros.cliente_emissor);  // #105
  add('demanda_acao', filtros.demanda_acao);        // #52 drill-down
  add('em_atraso', filtros.em_atraso);              // #52 drill-down
  add('criticas', filtros.criticas);                // #52 drill-down
  add('tem_custo', filtros.tem_custo);              // #52-financeiro drill-down
  add('data_inicio', filtros.data_inicio);
  add('data_fim', filtros.data_fim);
  add('eficacia', filtros.eficacia);
  add('busca', filtros.busca);
  return params;
}

export const ncService = {
  // Processar NC completa
  processarNC: async (formData) => {
    try {
      const response = await api.post('/api/nc/processar-completa', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Log da resposta
      if (response.status === 202) {
        logger.logPostResponse(response.data);
      } else {
        logger.info('POST /processar-completa (200 Sync)', {
          status: response.status,
          nc_id: response.data.id,
          workflow_status: response.data.workflow_status
        });
      }

      return response.data;
    } catch (error) {
      logger.logPostError(error);
      throw error;
    }
  },

  // Buscar NC por ID
  getNC: async (ncId) => {
    const response = await api.get(`/api/nc/${ncId}`);
    return response.data;
  },

  // #243 — estado do relógio de SLA 8D (countdown). Retorna { nc_id, sla },
  // com sla=null quando o cliente não tem sla_8d_habilitado (feature off).
  getSla: async (ncId) => {
    const response = await api.get(`/api/v2/nc/${ncId}/sla`);
    return response.data;
  },

  // Listar NCs do cliente
  // #78a (Sessão 32): filtros + paginação server-side. `filtros` é um objeto;
  // só as chaves definidas (não undefined/null/'') são enviadas como query params.
  listNCs: async (clienteId, filtros = {}) => {
    const params = {};
    const add = (k, v) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'todos') params[k] = v;
    };
    add('skip', filtros.skip);
    add('limit', filtros.limit);
    add('status', filtros.status);
    add('severidade', filtros.severidade);
    add('origem_tipo', filtros.origem_tipo);
    add('fornecedor_id', filtros.fornecedor_id);
    add('cliente_emissor', filtros.cliente_emissor);  // #105
    add('demanda_acao', filtros.demanda_acao);        // #52 drill-down
    add('em_atraso', filtros.em_atraso);              // #52 drill-down
    add('criticas', filtros.criticas);                // #52 drill-down
    add('tem_custo', filtros.tem_custo);              // #52-financeiro drill-down
    add('categoria_impacto', filtros.categoria_impacto);  // #108 drill por categoria
    add('sort', filtros.sort);                        // #137: ordenação por custo
    add('data_inicio', filtros.data_inicio);
    add('data_fim', filtros.data_fim);
    add('eficacia', filtros.eficacia);
    add('busca', filtros.busca);
    const response = await api.get(`/api/nc/cliente/${clienteId}/ncs`, { params });
    return response.data;
  },

  // #52 (Sessão 33): resumo do painel (Dashboard) numa request.
  getPainel: async (clienteId) => {
    const response = await api.get(`/api/nc/cliente/${clienteId}/painel`);
    return response.data;
  },

  // #238: carrega (idempotente) a NC de EXEMPLO de onboarding no tenant do
  // usuário. Retorna {nc_id, numero_nc, ja_existia}.
  carregarExemplo: async () => {
    const response = await api.post('/api/nc/exemplo');
    return response.data;
  },

  // #78a: lista fornecedores do tenant pra popular o dropdown de filtro
  listFornecedores: async (clienteId) => {
    const params = {};
    if (clienteId) params.cliente_id = clienteId;
    const response = await api.get('/api/nc/fornecedores/list', { params });
    return response.data;
  },

  // #105: lista os clientes emissores distintos do tenant pro dropdown de filtro
  listClientesEmissores: async (clienteId) => {
    const params = {};
    if (clienteId) params.cliente_id = clienteId;
    const response = await api.get('/api/nc/clientes-emissores/list', { params });
    return response.data;
  },

  // #78b: baixa o CSV (blob) do conjunto filtrado. responseType blob carrega o JWT
  // via interceptor; o componente cria object URL e dispara o download.
  exportarNCsCsv: async (clienteId, filtros = {}) => {
    const params = { ...buildExportParams(filtros), formato: 'csv' };
    const response = await api.get(`/api/nc/cliente/${clienteId}/ncs/export`, {
      params,
      responseType: 'blob',
    });
    return response.data; // Blob
  },

  // #78b: dados (7 colunas + meta) pro PDF renderizado no ListaPrintable.
  exportarNCsJson: async (clienteId, filtros = {}) => {
    const params = { ...buildExportParams(filtros), formato: 'json' };
    const response = await api.get(`/api/nc/cliente/${clienteId}/ncs/export`, { params });
    return response.data;
  },

  // #179: import em massa de NCs via CSV (migração da planilha legada).
  // Baixa o template (blob; responseType carrega o JWT via interceptor).
  baixarTemplateImportCsv: async () => {
    const response = await api.get('/api/v2/nc/importar-csv/template', { responseType: 'blob' });
    return response.data; // Blob
  },

  // confirmar=false → preview (não grava); confirmar=true → cria em lote.
  importarCsv: async (file, confirmar = false) => {
    const formData = new FormData();
    formData.append('arquivo', file);
    formData.append('confirmar', confirmar ? 'true' : 'false');
    const response = await api.post('/api/v2/nc/importar-csv', formData);
    return response.data;
  },

  // Listar todos os clientes (para auditor)
  listClientes: async () => {
    const response = await api.get('/api/nc/clientes/list');
    return response.data;
  },

  // Enviar feedback
  submitFeedback: async (ncId, feedbackData) => {
    const formData = new FormData();
    formData.append('causa_raiz_validada', feedbackData.causa_raiz_validada);
    formData.append('acoes_aprovadas', JSON.stringify(feedbackData.acoes_aprovadas));
    if (feedbackData.feedback_humano) {
      formData.append('feedback_humano', feedbackData.feedback_humano);
    }

    const response = await api.put(`/api/nc/${ncId}/feedback`, formData);
    return response.data;
  },

  // Upload de arquivo
  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/api/nc/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Upgrade de NC para Modo Investigação
  investigarNC: async (ncId) => {
    const response = await api.post(`/api/v2/nc/${ncId}/investigar`);
    return response.data;
  },

  // Edição granular da análise v4.0 (Full Analysis Override)
  // Fase 2A (#188) — reanalisarPorques: deriva nova causa raiz a PARTIR dos
  // porquês editados (modo Registro). Espelha reanalisarParcial (que parte do
  // 6M); mapeado pro flag reanalisar_porques no body. O ciclo de confirmação
  // (preview → confirmar_reanalise + preview_aceito) é o mesmo.
  // #189 P2 — regenAcoes: handshake do regen seletivo de ações. True = este
  // front SABE exibir as ações regeneradas no preview; o backend só inclui/
  // aplica ações quando recebe o handshake E a flag env do ambiente está ON.
  editarAnalise: async (ncId, edicoes, { override = false, reanalisarParcial = false, reanalisarPorques = false, confirmarReanalise = false, previewAceito = null, regenAcoes = false, atribuicao = null } = {}) => {
    const response = await api.put(`/api/v2/nc/${ncId}/editar-analise`, {
      edicoes,
      override,
      reanalisar_parcial: reanalisarParcial,
      reanalisar_porques: reanalisarPorques,
      confirmar_reanalise: confirmarReanalise,
      preview_aceito: previewAceito,
      regen_acoes: regenAcoes,
      // #275 F1 — proveniência do "puxar da resposta do fornecedor":
      // backend grava tipo_correcao='puxado_fornecedor' na trilha.
      atribuicao,
    });
    return response.data;
  },

  // Decisão 1 Fase 1B (Sessão 22) — cascata IA: regenera analise_completa_v2
  // inteira via 1 chamada IA. Backend grava snapshot pré-cascata (Fase 1A)
  // embutido em analise_completa_v2._snapshot_pre_cascata_ia pra permitir
  // restore via restaurarVersaoAnterior().
  //
  // Feature flag backend: CASCATA_IA_HABILITADA=true. Se off, retorna 503.
  // Frontend gate adicional: REACT_APP_CASCATA_IA_HABILITADA (default 'false').
  //
  // motivo: identificador de auditoria (ex: 'edicao_causa_raiz').
  // camposPreservados: paths dot-notation (ex: ['causa_raiz.declaracao']) —
  //   backend reaplica valores do estado anterior nestes campos pós-IA.
  cascataIA: async (ncId, { motivo = 'cascata_ia', camposPreservados = [] } = {}) => {
    const response = await api.post(`/api/v2/nc/${ncId}/cascata-ia`, {
      motivo,
      campos_preservados: camposPreservados,
    });
    return response.data;
  },

  // Decisão 1 Fase 1A — restaura snapshot pré-cascata IA (1x consumido).
  // Só funciona em NCs em status rascunho. 404 se snapshot já consumido ou
  // nunca existiu.
  restaurarVersaoAnterior: async (ncId) => {
    const response = await api.post(`/api/v2/nc/${ncId}/restaurar-versao-anterior`);
    return response.data;
  },

  // === #64 Feature 3 — convite a fornecedor (autenticado, admin+operador) ===

  // Cria convite. Retorna o invite COM public_url (token cru disponível 1x).
  // Backend dispara email best-effort; public_url volta pro tenant copiar.
  // #64 S4: mensagem_instrucao opcional — texto livre exibido ao fornecedor.
  convidarFornecedor: async (ncId, { supplier_email, supplier_nome, mensagem_instrucao }) => {
    const response = await api.post(`/api/v2/nc/${ncId}/invite-supplier`, {
      supplier_email,
      supplier_nome,
      mensagem_instrucao,
    });
    return response.data;
  },

  // Lista convites de uma NC (qualquer papel autenticado). public_url null.
  listarConvitesFornecedor: async (ncId) => {
    const response = await api.get(`/api/v2/nc/${ncId}/invite-supplier`);
    return response.data;
  },

  // Revoga um convite (admin+operador). Link público para de funcionar.
  revogarConviteFornecedor: async (ncId, inviteId) => {
    const response = await api.post(`/api/v2/nc/${ncId}/invite-supplier/${inviteId}/revoke`);
    return response.data;
  },
};

// Sessão 24 — Módulo Ocorrências MVP (TD104). Endpoints v3 paralelos
// aos de NC. Sem IA na triagem no MVP — operador decide manual.
export const ocorrenciaService = {
  listar: async ({ statusTriagem, origem, skip = 0, limit = 50 } = {}) => {
    const params = {};
    if (statusTriagem) params.status_triagem = statusTriagem;
    if (origem) params.origem = origem;
    if (skip) params.skip = skip;
    if (limit) params.limit = limit;
    const response = await api.get('/api/v3/ocorrencia', { params });
    return response.data;
  },

  get: async (id) => {
    const response = await api.get(`/api/v3/ocorrencia/${id}`);
    return response.data;
  },

  criar: async (payload) => {
    const response = await api.post('/api/v3/ocorrencia', payload);
    return response.data;
  },

  editar: async (id, payload) => {
    const response = await api.patch(`/api/v3/ocorrencia/${id}`, payload);
    return response.data;
  },

  mudarStatus: async (id, statusTriagem) => {
    const response = await api.patch(`/api/v3/ocorrencia/${id}/status`, {
      status_triagem: statusTriagem,
    });
    return response.data;
  },

  promover: async (id) => {
    const response = await api.post(`/api/v3/ocorrencia/${id}/promover`);
    return response.data;
  },

  // Sessão 24 follow-up: soft-delete. Backend bloqueia (409) se a
  // ocorrência foi promovida pra NC (histórico depende).
  excluir: async (id) => {
    await api.delete(`/api/v3/ocorrencia/${id}`);
  },

  // === Tags (Fase A backend #58 + Fase B IA #62 + Fase C frontend #63) ===

  // Lista tags associadas à ocorrência (todas as fontes).
  listarTags: async (id) => {
    const response = await api.get(`/api/v3/ocorrencia/${id}/tags`);
    return response.data;
  },

  // Adiciona tag. fonte default 'manual'; 'ia_sugerida_confirmada' quando
  // o usuário aceita uma sugestão da IA (#62 Fase B).
  adicionarTag: async (id, { tipo, nome, fonte = 'manual' }) => {
    const response = await api.post(`/api/v3/ocorrencia/${id}/tags`, {
      tipo,
      nome,
      fonte,
    });
    return response.data;
  },

  // Remove associação tag↔ocorrência (ProcessTag em si não é afetada).
  removerTag: async (id, occurrenceTagId) => {
    await api.delete(`/api/v3/ocorrencia/${id}/tags/${occurrenceTagId}`);
  },

  // #62 Fase B — IA propõe tags (SYNC, ~5-15s). Backend mapeia:
  //   504 → IA demorou demais (tente novamente)
  //   502 → IA indisponível
  // Retorna { sugestoes: [{tipo, nome, nome_normalizado, confianca, ja_associada}], latencia_ms, modelo }
  sugerirTags: async (id) => {
    const response = await api.post(`/api/v3/ocorrencia/${id}/tags/sugerir`);
    return response.data;
  },
};

// #79/#148 — Administração da plataforma (super-admin). Hoje só "criar conta"
// (tenant + 1º admin), via POST /api/account/admin/client (gated por
// _require_superadmin no backend). O token de super-admin entra automático
// pelo interceptor de request.
export const adminService = {
  criarConta: async (payload) => {
    const response = await api.post('/api/account/admin/client', payload);
    return response.data;
  },

  // Central de Notificações (backend#306) — ações de super-admin.
  // Cria um broadcast (notificação para todos os tenants/usuários).
  // body { titulo, corpo, tipo } com tipo ∈ update|issue|info.
  criarNotificacao: async ({ titulo, corpo, tipo }) => {
    const response = await api.post('/api/account/admin/notification', {
      titulo,
      corpo,
      tipo,
    });
    return response.data;
  },

  // Lista TODAS as notificações (visão de super-admin, inclui inativas).
  listarNotificacoesAdmin: async () => {
    const response = await api.get('/api/account/admin/notifications');
    return response.data;
  },

  // Desativa uma notificação (deixa de aparecer pros usuários).
  desativarNotificacao: async (id) => {
    const response = await api.patch(`/api/account/admin/notification/${id}`, {
      ativo: false,
    });
    return response.data;
  },
};

// Central de Notificações (backend#306) — visão do usuário logado (qualquer
// role). O backend já FILTRA a visibilidade e devolve o estado de leitura por
// usuário; o frontend só consome `nao_lidas` + `notificacoes[].lida` (NÃO
// reimplementa a regra). O token entra automático pelo interceptor de request.
export const notificationService = {
  // GET → { nao_lidas: <int>, notificacoes: [{id, titulo, corpo, tipo, created_at, lida}] }
  // Já ordenado (mais novas primeiro).
  listar: async () => {
    const response = await api.get('/api/notifications');
    return response.data;
  },

  // Marca uma notificação como lida (idempotente).
  marcarLida: async (id) => {
    const response = await api.post(`/api/notifications/${id}/lida`);
    return response.data;
  },

  // Marca todas como lidas.
  marcarTodasLidas: async () => {
    const response = await api.post('/api/notifications/marcar-todas-lidas');
    return response.data;
  },
};

// #260 — Canal de suporte in-app. Envia o formulário curto pro backend, que
// encaminha por e-mail (Resend) anexando o contexto da sessão (usuário, tenant,
// plano) e setando Reply-To = e-mail do usuário. O token entra automático pelo
// interceptor de request.
export const supportService = {
  enviarSuporte: async ({ assunto, mensagem, tipo, pagina } = {}) => {
    const response = await api.post('/api/support/contact', {
      assunto,
      mensagem,
      tipo,
      pagina,
    });
    return response.data;
  },
};

// === Copiloto (#47/TD106) — chat read-only sobre UMA NC (perk Pro+) ===
// A IA explica/sugere; NUNCA altera a análise (F1 read-only). O gate Pro+ é
// no backend (403); o front decide chat × upsell via user.copiloto_liberado.
export const copilotoService = {
  // Abre (ou retorna) a sessão ativa de copiloto da NC. Idempotente.
  abrirSessao: async (ncId) => {
    const response = await api.post(`/api/nc/${ncId}/copiloto/sessao`);
    return response.data;
  },
  // Histórico da conversa da sessão ativa (ordem cronológica).
  listarMensagens: async (ncId) => {
    const response = await api.get(`/api/nc/${ncId}/copiloto/mensagens`);
    return response.data;
  },
  // Envia uma pergunta e recebe a resposta do copiloto (síncrono).
  enviarMensagem: async (ncId, content) => {
    const response = await api.post(`/api/nc/${ncId}/copiloto/mensagem`, { content });
    return response.data;
  },
  // #47 streaming — versão SSE: a resposta chega token-a-token via onChunk(delta).
  // Usa fetch (axios não faz streaming de body) + leitor de ReadableStream + parser
  // SSE incremental. Resolve a lentidão percebida sem trocar modelo. Só é chamado
  // quando user.copiloto_streaming_habilitado === true (senão usa enviarMensagem).
  // Retorna o payload do frame 'done' ({session_id, mensagem, turn_count} ou
  // {persistido:false}). Erros: HTTP não-200 → err.response.{status,data}; frame
  // 'error' do stream → err.streamDetail. O painel trata como no caminho síncrono.
  enviarMensagemStream: async (ncId, content, { onChunk } = {}) => {
    const token = authService.getToken();
    const resp = await fetch(`${API_URL}/api/nc/${ncId}/copiloto/mensagem/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ content }),
    });
    if (!resp.ok || !resp.body) {
      const err = new Error('copiloto_stream_http_error');
      err.response = { status: resp.status };
      try { err.response.data = await resp.json(); } catch (e) { /* sem corpo */ }
      throw err;
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let done = null;
    // parser SSE: frames separados por linha em branco (\n\n)
    for (;;) {
      const { value, done: streamDone } = await reader.read();
      if (streamDone) break;
      buffer += decoder.decode(value, { stream: true });
      let sep;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        let event = null;
        let dataRaw = null;
        for (const linha of frame.split('\n')) {
          if (linha.startsWith('event: ')) event = linha.slice(7).trim();
          else if (linha.startsWith('data: ')) dataRaw = linha.slice(6);
        }
        if (dataRaw == null) continue;
        let payload;
        try { payload = JSON.parse(dataRaw); } catch (e) { continue; }
        if (event === 'error') {
          const err = new Error('copiloto_stream_error');
          err.streamDetail = payload.detail;
          throw err;
        } else if (event === 'done') {
          done = payload;
        } else if (payload.delta && onChunk) {
          onChunk(payload.delta);
        }
      }
    }
    return done;
  },
};

export default api;
